if (!customElements.get('recipe-ugc')) {
  class RecipeUgc extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.rcu__track');
      this.bar = this.querySelector('.rcu__bar');
      this.slides = Array.from(this.querySelectorAll('.rcu__slide'));
      this.segments = Array.from(this.querySelectorAll('.rcu__seg'));
      this.drag = this.querySelector('drag-scroll');
      if (!this.track || !this.slides.length) return;

      if (this.drag) {
        this.drag.snapTo = (position, velocity) => this.nearest(position, velocity);
      }

      this.segments.forEach((seg, i) => {
        seg.addEventListener('click', () => {
          if (!this.targets || this.targets[i] === undefined) return;
          this.stopChain();
          this.goTo(this.targets[i]);
        });
      });

      this.frames = this.slides.map((slide) => slide.querySelector('deferred-media'));
      this.current = -1;
      this.chain = this.dataset.autoplay === 'true' && !this.reduced();
      if (this.chain) this.armChain();

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.measure();
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.measure());
        this.observer.observe(this.track);
      }
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.watcher) this.watcher.disconnect();
      if (this.observer) this.observer.disconnect();
    }

    reduced() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    armChain() {
      this.onPress = (event) => {
        this.pressX = event.clientX;
      };
      this.onPressMove = (event) => {
        if (this.pressX === undefined || !event.buttons) return;
        if (Math.abs(event.clientX - this.pressX) > 6) this.stopChain();
      };
      this.onPressEnd = () => {
        this.pressX = undefined;
      };
      this.onTouch = () => this.stopChain();
      this.onTrackClick = (event) => {
        const frame = event.target.closest('deferred-media');
        const index = frame ? this.frames.indexOf(frame) : -1;
        this.stopChain(index);
      };
      this.track.addEventListener('pointerdown', this.onPress);
      this.track.addEventListener('pointermove', this.onPressMove);
      this.track.addEventListener('pointerup', this.onPressEnd);
      this.track.addEventListener('touchmove', this.onTouch, { passive: true });
      this.track.addEventListener('click', this.onTrackClick, true);

      if (!('IntersectionObserver' in window)) {
        this.playIndex(0);
        return;
      }
      this.watcher = new IntersectionObserver(
        (entries) => {
          const shown = entries.some((entry) => entry.isIntersecting);
          if (!this.chain) return;
          if (shown) {
            if (this.current < 0) this.playIndex(0);
            else this.currentVideo()?.play().catch(() => {});
          } else {
            this.currentVideo()?.pause();
          }
        },
        { threshold: 0.25 }
      );
      this.watcher.observe(this);
    }

    stopChain(keep = -1) {
      if (!this.chain) return;
      this.chain = false;
      if (this.current !== keep) this.resetCurrent();
      this.current = -1;
    }

    currentVideo() {
      const frame = this.current >= 0 ? this.frames[this.current] : null;
      return frame ? frame.querySelector('video') : null;
    }

    resetCurrent() {
      if (this.current < 0) return;
      const frame = this.frames[this.current];
      const video = frame && frame.querySelector('video');
      if (video) {
        video.pause();
        video.remove();
      }
      if (frame) frame.removeAttribute('loaded');
      this.current = -1;
    }

    playIndex(index, hops = 0) {
      if (!this.chain || hops >= this.frames.length) return;
      const frame = this.frames[index];
      const template = frame && frame.querySelector('template');
      const source = template && template.content.querySelector('video');
      if (!source) {
        this.playIndex((index + 1) % this.frames.length, hops + 1);
        return;
      }
      this.resetCurrent();
      this.current = index;
      source.setAttribute('muted', '');
      frame.loadContent(false);
      source.removeAttribute('muted');
      const video = frame.querySelector('video');
      if (!video) {
        this.current = -1;
        return;
      }
      video.muted = true;
      video.addEventListener(
        'ended',
        () => {
          if (this.chain && this.current === index) this.playIndex((index + 1) % this.frames.length);
        },
        { once: true }
      );
      const playing = video.play();
      if (playing && playing.catch) {
        playing.catch(() => {
          if (this.current === index) this.stopChain();
        });
      }
      this.reveal(index);
    }

    reveal(index) {
      if (!this.targets) return;
      const slide = this.slides[index].getBoundingClientRect();
      const track = this.track.getBoundingClientRect();
      const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      const edge = parseFloat(getComputedStyle(this.track).paddingRight) || 0;
      if (slide.left >= track.left + pad - 1 && slide.right <= track.right - edge + 1) return;
      const last = this.targets[this.targets.length - 1];
      this.goTo(this.targets[index] !== undefined ? this.targets[index] : last);
    }

    goTo(left) {
      if (this.drag && typeof this.drag.glideTo === 'function') {
        this.drag.glideTo(left, 0);
        return;
      }
      this.track.scrollTo({ left, behavior: this.reduced() ? 'auto' : 'smooth' });
    }

    nearest(position, velocity) {
      if (!this.targets || !this.targets.length) return position;
      const current = this.track.scrollLeft;
      const ahead = this.targets.filter((t) => (velocity < -0.2 ? t > current + 1 : velocity > 0.2 ? t < current - 1 : true));
      const pool = ahead.length ? ahead : this.targets;
      let best = pool[0];
      let min = Infinity;
      pool.forEach((t) => {
        const d = Math.abs(t - position);
        if (d < min) {
          min = d;
          best = t;
        }
      });
      return best;
    }

    measure() {
      const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const origin = this.track.getBoundingClientRect().left - this.track.scrollLeft;

      this.targets = [];
      this.slides.forEach((slide) => {
        const target = slide.getBoundingClientRect().left - origin - pad;
        if (target <= max + 1) this.targets.push(Math.max(0, Math.min(target, max)));
      });
      if (!this.targets.length) this.targets.push(0);
      if (this.targets.length < this.segments.length && max - this.targets[this.targets.length - 1] > 1) {
        this.targets.push(max);
      }

      this.segments.forEach((seg, i) => {
        seg.hidden = i >= this.targets.length;
      });
      if (this.bar) this.bar.hidden = this.targets.length < 2;

      this.sync();
    }

    sync() {
      if (!this.targets) return;
      const left = this.track.scrollLeft;
      let best = 0;
      let min = Infinity;
      this.targets.forEach((target, i) => {
        const d = Math.abs(target - left);
        if (d < min) {
          min = d;
          best = i;
        }
      });
      this.segments.forEach((seg, i) => {
        const on = i === best && !seg.hidden;
        seg.classList.toggle('rcu__seg--on', on);
        seg.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
  }

  customElements.define('recipe-ugc', RecipeUgc);
}
